import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react'
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { useGenerateSignInLinkMutation } from '../../features/SpeechAgendaApiSlice.ts'
import LoadingSpinner from '../icons/LoadingSpinner.tsx'

type LoginStep = 'EMAIL' | 'ERROR_USER_NOT_FOUND' | 'ERROR' | 'COMPLETE'

export default function SignInWithLinkModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: (success: boolean) => void
}) {
  const [loginStep, setLoginStep] = useState<LoginStep>('EMAIL')
  const [email, setEmail] = useState('')

  const [generateLink, generateLinkResult] = useGenerateSignInLinkMutation()

  const sendSignInLink = async () => {
    await generateLink({ email })
  }

  useEffect(() => {
    if (generateLinkResult.isSuccess) {
      setLoginStep('COMPLETE')
    }
    if (generateLinkResult.isError) {
      setLoginStep('ERROR_USER_NOT_FOUND')
    }
  }, [generateLinkResult])

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onClose(false)
        setTimeout(() => {
          setLoginStep('EMAIL')
        }, 100)
      }}
      className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
      />
      <div className="fixed inset-0 w-screen overflow-y-auto z-10">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel>
            <div className="bg-white px-6 py-12 shadow-sm sm:rounded-lg sm:px-12 max-w-80">
              {/*Internal content of the modal*/}
              {loginStep === 'EMAIL' && (
                <DialogTitle
                  as="h3"
                  className="text-base font-semibold text-gray-900 mb-3">
                  Login using email link
                </DialogTitle>
              )}
              <div className="space-y-6 text-left">
                {/* EMAIL */}
                {loginStep == 'EMAIL' && (
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm/6 font-medium text-gray-900">
                      Email address
                    </label>
                    <div className="mt-2">
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="email"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                )}
                {loginStep == 'EMAIL' && (
                  <div>
                    <button
                      type="submit"
                      onClick={sendSignInLink}
                      disabled={generateLinkResult.isLoading}
                      className="flex w-full justify-center items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-xs hover:bg-indigo-500 disabled:bg-indigo-400 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                      {generateLinkResult.isLoading ? (
                        <LoadingSpinner />
                      ) : (
                        'Continue'
                      )}
                    </button>
                  </div>
                )}
                {loginStep == 'COMPLETE' && (
                  <div>
                    <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-green-100">
                      <CheckIcon
                        aria-hidden="true"
                        className="size-6 text-green-600"
                      />
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                      <DialogTitle
                        as="h3"
                        className="text-base font-semibold text-gray-900">
                        Login Link Sent
                      </DialogTitle>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          We've sent a login link to your email address. Please
                          check your inbox and click the link to complete your
                          login
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {(loginStep === 'ERROR' ||
                  loginStep === 'ERROR_USER_NOT_FOUND') && (
                  <div>
                    <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-red-100">
                      <XMarkIcon
                        aria-hidden="true"
                        className="size-6 text-red-600"
                      />
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                      <DialogTitle
                        as="h3"
                        className="text-base font-semibold text-gray-900">
                        Error Occurred
                      </DialogTitle>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          <p className="text-sm text-gray-500">
                            {loginStep === 'ERROR_USER_NOT_FOUND'
                              ? 'This email is not registered. Please sign up first'
                              : "We couldn't complete your login via email link. Please try signing in with another method, or contact our support team if you continue experiencing issues"}
                          </p>
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {(loginStep === 'ERROR' ||
                  loginStep === 'ERROR_USER_NOT_FOUND') && (
                  <div>
                    <button
                      type="submit"
                      onClick={() => {
                        onClose(false)
                        setTimeout(() => {
                          setLoginStep('EMAIL')
                        }, 100)
                      }}
                      className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                      Go back
                    </button>
                  </div>
                )}
                {loginStep === 'COMPLETE' && (
                  <div>
                    <button
                      type="submit"
                      onClick={() => {
                        onClose(true)
                        setTimeout(() => {
                          setLoginStep('EMAIL')
                        }, 100)
                      }}
                      className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                      Close this window
                    </button>
                  </div>
                )}
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  )
}
